/**
 * Licence / trial reconciliation.
 *
 * Historically the trial gate was read from localStorage only: editable by the
 * user, and it drifted from the advertised 14-day trial. This module makes the
 * SERVER the source of truth (subscriptions + entitlements, RLS-scoped) and
 * demotes the cached value to an offline fallback.
 *
 * The reconciliation itself is pure so it can be unit-tested without a database
 * — see tests/unit/licence-reconcile.test.mts.
 */

export type LicenceSource =
  /** Signed-in, on a trial. `trialEndAt` is the server's word, or null if unknown. */
  | { kind: 'trialing'; trialEndAt: string | null; planSlug?: string | null }
  /** Paid plan / activated licence. `expiresAt` null means lifetime. */
  | { kind: 'active'; expiresAt?: string | null; licenceType?: string | null }
  /** Server says the trial or licence has run out. */
  | { kind: 'expired' }
  /** No session, no subscription row, or the lookup failed. */
  | { kind: 'unknown' };

export interface LicenceState {
  mode: 'demo' | 'activated' | 'expired';
  daysRemaining: number;
  activatedAt?: string;
  expiresAt?: string;
  licenceKey?: string;
  licenceType?: 'yearly' | 'lifetime';
}

/** Matches the trial advertised on /pricing, /signup and /demo. */
export const TRIAL_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Unlimited sentinel used by the UI for lifetime licences. */
export const UNLIMITED_DAYS = 999;

export function daysUntil(iso: string, now: Date = new Date()): number {
  // Clamped at 0: a moment in the past is "no days left", never negative.
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS));
}

function demoFromCache(cached: LicenceState | null, now: Date): LicenceState {
  if (cached?.mode === 'demo' && cached.expiresAt) {
    const days = daysUntil(cached.expiresAt, now);
    return days > 0 ? { ...cached, daysRemaining: days } : { mode: 'expired', daysRemaining: 0 };
  }
  // No usable cache (signed out, or storage cleared): show a fresh trial window.
  // The server remains authoritative for anyone signed in — see kind:'trialing'.
  return {
    mode: 'demo',
    daysRemaining: TRIAL_DAYS,
    expiresAt: new Date(now.getTime() + TRIAL_DAYS * DAY_MS).toISOString(),
  };
}

/**
 * Resolve the effective licence state.
 *
 * Invariants:
 *  - a paid licence can never be downgraded by a stale cache
 *  - a server expiry is never extended by a newer-looking client value
 *  - 'unknown' never invents an entitlement: it falls back to cache/demo only
 */
export function reconcileLicence(
  source: LicenceSource,
  cached: LicenceState | null = null,
  now: Date = new Date()
): LicenceState {
  switch (source.kind) {
    case 'active': {
      const lifetime = source.licenceType === 'lifetime' || !source.expiresAt;
      if (lifetime) {
        return {
          mode: 'activated',
          daysRemaining: UNLIMITED_DAYS,
          licenceType: 'lifetime',
          licenceKey: cached?.licenceKey,
        };
      }
      const days = daysUntil(source.expiresAt!, now);
      if (days <= 0) {
        return { mode: 'expired', daysRemaining: 0, expiresAt: source.expiresAt! };
      }
      return {
        mode: 'activated',
        daysRemaining: days,
        expiresAt: source.expiresAt!,
        licenceType: 'yearly',
        activatedAt: cached?.activatedAt,
        licenceKey: cached?.licenceKey,
      };
    }

    case 'trialing': {
      if (!source.trialEndAt) return demoFromCache(cached, now);
      const days = daysUntil(source.trialEndAt, now);
      if (days <= 0) {
        return { mode: 'expired', daysRemaining: 0, expiresAt: source.trialEndAt };
      }
      return { mode: 'demo', daysRemaining: days, expiresAt: source.trialEndAt };
    }

    case 'expired':
      return { mode: 'expired', daysRemaining: 0 };

    case 'unknown':
    default:
      return demoFromCache(cached, now);
  }
}

/** Map an entitlement snapshot (server) onto a LicenceSource. */
export function sourceFromEntitlements(snapshot: {
  status: string | null;
  trialEndAt?: string | null;
  currentPeriodEnd?: string | null;
  planSlug?: string | null;
}): LicenceSource {
  const status = (snapshot.status ?? '').toLowerCase();
  if (status === 'active') {
    return {
      kind: 'active',
      expiresAt: snapshot.currentPeriodEnd ?? null,
      licenceType: 'yearly',
    };
  }
  if (status === 'trialing') {
    return {
      kind: 'trialing',
      trialEndAt: snapshot.trialEndAt ?? null,
      planSlug: snapshot.planSlug,
    };
  }
  if (['expired', 'canceled', 'cancelled', 'unpaid', 'past_due'].includes(status)) {
    return { kind: 'expired' };
  }
  return { kind: 'unknown' };
}
